import React, { Suspense, useEffect, useState, useRef } from "react";
import { Router, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import i18next from "i18next";

import App from "./App";
import { Login } from "./pages/Login/Login";
import { Loading } from "./pages/Loading/Loading";
import { ErrorTenant } from "./pages/ErrorTenant/ErrorTenant";

import {
  getTenantAsync,
  getTenantLoadingStatus,
  getTenantInfo,
  setTenantStatus,
} from "./reducers/tenant";
import { getVersion, updateVersion } from "./reducers/versions";
import { getLoginStatus, getToken } from "./reducers/login";
import { getTenantName, updateTenant } from "./reducers/app";
import { getBusinessAsync } from "./reducers/business";
import { getSettingsAsync } from "./reducers/settings";
import {
  getTradersLoadingStatus,
  getTradersAsync,
  hasMultipleCustomers,
  getTradersListTop1,
  setTradersStatus,
  getTradersList,
} from "./reducers/traders";
import {
  getTranslations,
  setTranslationStatus,
  translationAsync,
} from "./reducers/translations";
import { setCurrentTrader } from "./reducers/trader";

import {
  APP_LOADING_STATUS,
  COMMON_STATUS,
  OVERALL_PAGE_FLOW,
  SEVERITY_STATUS,
} from "./constants/status";

import { appVersion } from "./helper/config";
import history from "./helper/history";
import {
  clearEverything,
  clearOnLogout,
  clearTrader,
} from "./actions/dispatch/dispatchCommon";
import axios from "axios";
import { updateIP } from "./reducers/ip";
import Invitation from "./pages/Account/Invitation/Invitation";
import { useAppThunkDispatch } from "./store/store";
import {
  getTokenExpiryTimeLeftSeconds,
  getTokenExpiryTimer,
  isTokenExpired,
  parseJwtToken,
} from "./actions/common";
import { useTranslation } from "react-i18next";
import { ModalLogin } from "./components/Login/ModalLogin";
import { logoutUser } from "./reducers/common";
import { UserNoAddressDialog } from "./components/Dialog/UserNoAddressDialog";

const AppWrapper = () => {
  // const dispatch = useDispatch();
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation("common");

  const language_current_store = useSelector(getTranslations);
  const [current_lang, setCurrentLang] = useState("en");

  const current_app_version = useSelector(getVersion);

  const parse_url_path = window.location.pathname.split("/");
  const client_name_from_url: string = parse_url_path[2];

  const current_client_name = useSelector(getTenantName);
  const client_loading_status = useSelector(getTenantLoadingStatus);
  const client_info = useSelector(getTenantInfo);

  const login_token = useSelector(getToken);
  const logged = useSelector(getLoginStatus);
  const [token, setToken] = useState<string | undefined>("");

  const has_multiple_customers = useSelector(hasMultipleCustomers);
  const customer_loading_status = useSelector(getTradersLoadingStatus);
  const trader = useSelector(getTradersList);

  const [app_loading_status, setAppLoadingStatus] = useState<string>(
    APP_LOADING_STATUS.INITIAL
  );

  const [show_customers, setShowCustomers] = useState<boolean>(false);

  const current_customer = useSelector(getTradersListTop1);
  const [hasIp, setHasIP] = useState(false);

  const [ip_Call, setIPCALL] = useState<number>(0);
  const [pageFlow, setPageFlow] = useState<string>(OVERALL_PAGE_FLOW.START);

  const [displayDialog, setDisplayDialog] = useState<boolean>(false);
  const [timeoutTokenExpiry, setTimeoutTokenExpiry] =
    useState<ReturnType<typeof setTimeout>>();
  const [isDisplayUserNoAccessDialog, setIsDisplayUserNoAccessDialog] =
    useState<boolean>(false);

  //on unload check if browser close or tab close
  //this will determine if we need to clear cache and get data from the server
  //we compare the time
  //but need further research similar to this because this sometimes does not work

  window.onload = () => {
    localStorage.setItem("ms-new-storagetime", new Date().getTime().toString());

    if (language_current_store.status === COMMON_STATUS.LOADING) {
      dispatch(setTranslationStatus(COMMON_STATUS.INITIAL));
    }
  };

  window.onbeforeunload = () => {
    const localStorageTime: string | null =
      localStorage.getItem("ms-new-storagetime");
    if (localStorageTime != null && localStorageTime != undefined) {
      const minute = 1000 * 60;
      const currentTime = new Date().getTime(),
        timeDifference = currentTime - parseFloat(localStorageTime);

      if (timeDifference / minute < 25) {
        //Browser Closed
        localStorage.setItem(
          "ms-new-storagetime",
          new Date().getTime().toString()
        );
      } else {
        //Browser Tab Closed
        localStorage.removeItem("ms-new-storagetime");
      }
    } else {
      localStorage.setItem(
        "ms-new-storagetime",
        new Date().getTime().toString()
      );
    }
  };

  //this will determine if we need to clear cache and get data from the server
  //this will just called once on page render
  //we also check if the app version is updated and if yes we need to clear the cahce and get data from server

  useEffect(() => {
    const localStorageTime: string | null =
      localStorage.getItem("ms-new-storagetime");
    const is_version_updated = current_app_version !== appVersion;

    if (localStorageTime === null || is_version_updated) {
      setToken("");
      clearEverything(dispatch);
      if (is_version_updated) dispatch(updateVersion(appVersion));
    }
    setAppLoadingStatus(APP_LOADING_STATUS.VERSION_DONE);
  }, [current_app_version, dispatch]);

  /**
   * ----------------------------------------------------------------
   * * UseEffect
   * For setting token expiry timeout login modal dialog
   * and checking if token is still invalid/expired;
   * If token invalid/expired when page is refreshed, logout;
   * ----------------------------------------------------------------
   */
  useEffect(() => {
    // console.log("%c UseEffect setTimeout", 'background-color: yellow; color: black; ')

    setDisplayDialog(false);

    if (token !== undefined && token !== "" && token !== null) {
      // * clear timer if exist, before setting a timer
      if (timeoutTokenExpiry) {
        clearTimeout(timeoutTokenExpiry);
      }

      // * set timer for token expiration
      setTimeoutTokenExpiry(getTokenExpiryTimer(token, setDisplayDialog));
    } else if (!login_token || isTokenExpired(login_token)) {
      // console.log("%c TOKEN!! --- ", 'background-color: orange; color: black;', token, login_token)

      // * When page is refreshed, stays on the same page, show the modal login
      // ? Comment out the other if this is used/preffered
      // setDisplayDialog(true)

      // * When page is refreshed, proceeds to [logout] function
      logout();
    }
  }, [token, login_token]);

  //check first if the app name is same otherwise clear local storage cache
  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.VERSION_DONE) {
      if (current_client_name && current_client_name !== client_name_from_url) {
        setToken("");
        clearEverything(dispatch);
      }
      if (client_loading_status === COMMON_STATUS.FAILED)
        dispatch(setTenantStatus(COMMON_STATUS.INITIAL));
      setAppLoadingStatus(APP_LOADING_STATUS.CLIENT_DONE);
    }
  }, [
    app_loading_status,
    client_loading_status,
    client_name_from_url,
    current_client_name,
    dispatch,
  ]);

  //Get the IP address of the user
  useEffect(() => {
    const getData = async () => {
      try {
        const res = await axios.get("https://geolocation-db.com/json/");
        dispatch(updateIP(res.data.IPv4));
        setHasIP(true);
      } catch (err: any) {
        // console.log(err.message);
        // add to log in API
        setIPCALL(ip_Call + 1);
      }
    };
    if (!hasIp) {
      if (ip_Call < 5) getData();
    }
  }, [dispatch, hasIp, ip_Call]);

  useEffect(() => {
    let searchUrl = window.location.search;
    let _lang = current_lang;

    if (searchUrl) {
      searchUrl = searchUrl.replace("?", "");
      const searchParams = searchUrl.split("&");
      searchParams.map((searchParam) => {
        const sp = searchParam.split("=");
        if (sp[0] === "lang") {
          _lang = sp[1];
          setCurrentLang(_lang);
          dispatch(setTranslationStatus(COMMON_STATUS.INITIAL));
        }
      });
    }
  }, []);

  useEffect(() => {
    if (logged === COMMON_STATUS.LOGIN) {
      // console.log("%c useEffect :: hasMultipleCustomers = %o", 'background-color: yellow; color: black;', has_multiple_customers, trader, trader.some(trader => trader.addresses.length > 0), logged)

      if (
        trader &&
        trader.length > 0 &&
        !trader.some((trader) => trader.addresses.length > 0)
      ) {
        setIsDisplayUserNoAccessDialog(true);
      } else {
        setIsDisplayUserNoAccessDialog(false);
      }
    }
  }, [logged, app_loading_status]);

  //Handling Language
  useEffect(() => {
    const initLanguage = (lang: string, data: any) => {
      setTimeout(() => {
        setCurrentLang(
          lang != null && lang != undefined && lang != "undefined" ? lang : "en"
        );
      }, 500);

      i18next.init({
        interpolation: { escapeValue: false }, // React already does escaping
        lng: "en", // language to use
        resources: {
          en: {
            common: data, // 'common' is our custom namespace
          },
        },
      });
    };

    const getBackupLanguage = (stat: string) => {
      axios
        .get("../assets/en-language.json")
        .then((res) => {
          dispatch(setTranslationStatus(stat));
          initLanguage("en", res.data);
        })
        .catch((err) => {
          console.log(err);
        });
    };

    const getLanguage = () => {
      dispatch(
        translationAsync({
          tenantName: client_name_from_url,
          language: current_lang,
        })
      );
    };

    if (language_current_store.status === COMMON_STATUS.INITIAL) {
      getLanguage();
    } else if (language_current_store.status === COMMON_STATUS.FAILED) {
      if (language_current_store.apiCallCount < 5) getLanguage();
      else {
        // should add on api call error logs
        getBackupLanguage(COMMON_STATUS.DONE);
      }
    } else if (language_current_store.status === COMMON_STATUS.DONE) {
      if (
        language_current_store.value.language &&
        language_current_store.value.language
      ) {
        initLanguage(
          language_current_store.value.language,
          language_current_store.value.translations
        );
      } else {
        dispatch(setTranslationStatus(COMMON_STATUS.INITIAL));
      }
    }
  }, [
    client_name_from_url,
    current_lang,
    dispatch,
    language_current_store.apiCallCount,
    language_current_store.status,
    language_current_store.value.language,
    language_current_store.value.translations,
  ]);

  useEffect(() => {
    setToken(login_token);
    if (login_token === "") {
      setAppLoadingStatus(APP_LOADING_STATUS.CLIENT_DONE);
      dispatch(setTenantStatus(COMMON_STATUS.INITIAL));
    }
  }, [dispatch, login_token]);

  //let get tenant detail for logo, name, currency (business details that will use on the app)
  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.CLIENT_DONE) {
      switch (client_loading_status) {
        case COMMON_STATUS.INITIAL:
          //Get Tenant Detail
          dispatch(getTenantAsync(client_name_from_url));
          break;
        case COMMON_STATUS.LOADED:
          //check if tenant exist in the
          if (client_info && client_info.name) {
            dispatch(updateTenant(client_name_from_url));
            dispatch(setTenantStatus(COMMON_STATUS.DONE));
            setTimeout(() => {
              setAppLoadingStatus(APP_LOADING_STATUS.TENANT_INFO_DONE);
            }, 300);
          }
          //If not show tenant doesn"t exist - contact admin
          else setAppLoadingStatus(APP_LOADING_STATUS.TENANT_INFO_FAILED);
          break;
        case COMMON_STATUS.FAILED:
          setAppLoadingStatus(APP_LOADING_STATUS.TENANT_INFO_FAILED);
          break;
        case COMMON_STATUS.DONE:
          setAppLoadingStatus(APP_LOADING_STATUS.TENANT_INFO_DONE);
          break;
      }
    }
  }, [
    app_loading_status,
    client_loading_status,
    dispatch,
    client_info,
    client_name_from_url,
  ]);

  //check if user is logged in and get list of clients
  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.TENANT_INFO_DONE) {
      if (token) {
        if (customer_loading_status === COMMON_STATUS.INITIAL) {
          setAppLoadingStatus(APP_LOADING_STATUS.TRADERS_START);
          dispatch(
            getBusinessAsync({ tenantName: client_name_from_url, token: token })
          );
          dispatch(
            getSettingsAsync({ tenantName: client_name_from_url, token: token })
          );
          dispatch(
            getTradersAsync({ tenantName: client_name_from_url, token: token })
          );
        } else if (customer_loading_status === COMMON_STATUS.DONE)
          setAppLoadingStatus(APP_LOADING_STATUS.TRADERS_DONE);
      }

      const path = window.location.pathname.toLowerCase();
      const inviUrl =
        `${process.env.PUBLIC_URL}/${current_client_name}/invitation`.toLowerCase();
      if (path === inviUrl) {
        setPageFlow(OVERALL_PAGE_FLOW.INVITATION);
      } else {
        if (!token) {
          setPageFlow(OVERALL_PAGE_FLOW.LOGIN);
        } else {
          setPageFlow(OVERALL_PAGE_FLOW.LOGGED);
        }
      }
    }
  }, [
    app_loading_status,
    token,
    customer_loading_status,
    dispatch,
    client_name_from_url,
    current_client_name,
  ]);

  //all client listed
  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.TRADERS_START) {
      if (
        customer_loading_status === COMMON_STATUS.LOADED ||
        customer_loading_status === COMMON_STATUS.FAILED
      ) {
        setAppLoadingStatus(APP_LOADING_STATUS.TRADERS_DONE);
        dispatch(setTradersStatus(COMMON_STATUS.DONE));
      }
    }
  }, [app_loading_status, customer_loading_status, dispatch]);

  //check if tenant has multiple traders or not
  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.TRADERS_DONE) {
      if (
        parse_url_path.length <= 3 ||
        (parse_url_path.length === 4 && parse_url_path[3].trim().length === 0)
      ) {
        clearTrader(dispatch);
      }
      if (customer_loading_status === COMMON_STATUS.LOADED) {
        setShowCustomers(has_multiple_customers);
        if (!has_multiple_customers) {
          dispatch(setCurrentTrader(current_customer));
        }
        setTimeout(() => {
          setAppLoadingStatus(APP_LOADING_STATUS.LOADED_ALL_DATA);
        }, 200);
      } else if (
        customer_loading_status === COMMON_STATUS.DONE &&
        app_loading_status != APP_LOADING_STATUS.LOADED_ALL_DATA
      ) {
        setShowCustomers(has_multiple_customers);
        if (!has_multiple_customers) {
          dispatch(setCurrentTrader(current_customer));
        }
        setTimeout(() => {
          setAppLoadingStatus(APP_LOADING_STATUS.LOADED_ALL_DATA);
        }, 200);
      }
    }
  }, [
    app_loading_status,
    customer_loading_status,
    has_multiple_customers,
    current_customer,
    parse_url_path,
    dispatch,
  ]);

  useEffect(() => {
    if (app_loading_status === APP_LOADING_STATUS.LOADED_ALL_DATA) {
      const path = window.location.pathname.toLowerCase();
      const inviUrl =
        `${process.env.PUBLIC_URL}/${current_client_name}/invitation`.toLowerCase();
      if (path === inviUrl) {
        setPageFlow(OVERALL_PAGE_FLOW.INVITATION);
      } else {
        if (!token) {
          setPageFlow(OVERALL_PAGE_FLOW.LOGIN);
        } else {
          setPageFlow(OVERALL_PAGE_FLOW.LOGGED);
        }
      }
    }
  }, [app_loading_status, current_client_name, token]);

  const logout = () => {
    clearOnLogout(dispatch);
    setToken("");
    setAppLoadingStatus(APP_LOADING_STATUS.CLIENT_DONE);
    setTimeout(() => {
      let isInvitation =false;
      if(parse_url_path.length>=4){
        isInvitation = parse_url_path[3].toLowerCase() == 'invitation';
      }
      if(!isInvitation)
        history.push(`${process.env.PUBLIC_URL}/${client_name_from_url}/login`);
    }, 200);
  };

  const onSetLanguage = (lang: string) => {
    setCurrentLang(lang);
    dispatch(setTranslationStatus(COMMON_STATUS.INITIAL));
  };

  //check first if link is default/trader/cart default

  switch (app_loading_status) {
    case APP_LOADING_STATUS.TENANT_INFO_DONE:
      if (pageFlow === OVERALL_PAGE_FLOW.LOGIN) {
        return <Login setToken={setToken} />;
      } else if (pageFlow === OVERALL_PAGE_FLOW.INVITATION) {
        return <Invitation setToken={setToken} />;
      }
      return <Loading />;
    case APP_LOADING_STATUS.LOADED_ALL_DATA:
      if (pageFlow === OVERALL_PAGE_FLOW.LOGIN) {
        return <Login setToken={setToken} />;
      } else if (pageFlow === OVERALL_PAGE_FLOW.INVITATION) {
        return <Invitation setToken={setToken} />;
      }
      return (
        <>
          {!isDisplayUserNoAccessDialog ? (
            <>
              <Router history={history}>
                <Suspense
                  fallback={
                    <div>
                      <Loading></Loading>
                    </div>
                  }
                >
                  <App
                    onSetLanguage={onSetLanguage}
                    currentLang={current_lang}
                    setToken={setToken}
                    setAppLoadingStatus={setAppLoadingStatus}
                    showCustomers={show_customers}
                    clientFullName={client_info ? client_info.name || "" : ""}
                    clientName={client_name_from_url}
                    logout={logout}
                  />
                </Suspense>
              </Router>

              <ModalLogin
                displayDialog={displayDialog}
                setDisplayDialog={setDisplayDialog}
                setToken={setToken}
                logout={logout}
              />
            </>
          ) : (
            <UserNoAddressDialog
              logout={logout}
              isDisplay={isDisplayUserNoAccessDialog}
            />
          )}
        </>
      );
    case APP_LOADING_STATUS.TENANT_INFO_FAILED:
      return <ErrorTenant clientName={client_name_from_url} />;
    default:
      return <Loading />;
  }
};

export default AppWrapper;
